import React from "react";
import { useLanguage, useT } from "../../lib/LanguageContext";
import { AFFILIATE_PATHS } from "../../affiliate/contract";
import LoadFailure from "../../components/ui/LoadFailure";
import usePagedList from "../../hooks/usePagedList";
import Pager from "../../components/ui/Pager";
import { affiliateErrorText } from "../../admin/affiliate/errorText";
import { fmtDate, EMPTY } from "../../admin/affiliate/format";

/**
 * The archived v0 commission rows — READ-ONLY, and rendered exactly as they were STORED.
 *
 * `amountLegacy` IS A FLOAT and its name says so. These rows have no key back to the sale
 * that funded them, so no cents figure can be derived from them without inventing precision
 * that was lost, and nothing here is ever posted to the ledger. The float is printed with
 * its stored currency and NOT through `formatMoneyCode`: that formatter takes integer minor
 * units, and feeding it a float dollar amount would silently print a hundredth of the
 * figure an operator is about to settle by hand.
 *
 * The French `note` and the pre-flip currency are preserved verbatim: this is the document
 * an operator settles the pre-v1 program from, once, and translating or restating it would
 * model data that never existed.
 */
export default function LegacyCommissionsPanel() {
  const t = useT("superadmin");
  const tv = useT("common");
  const { lang } = useLanguage();
  const list = usePagedList(AFFILIATE_PATHS.adminLegacyCommissions);

  return (
    <section className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="sa-affiliate-legacy">
      <div className="px-5 py-4 border-b border-slate-200">
        <h2 className="text-xl text-slate-800">{t("saff.legacy.title")}</h2>
        <p className="text-xs text-slate-400 mt-0.5">{t("saff.legacy.desc")}</p>
      </div>

      {list.loadError ? (
        <div className="p-5">
          <LoadFailure
            message={affiliateErrorText(list.loadError, { t, tv, prefix: "saff", fallback: tv("errors.loadFailed") })}
            onRetry={list.reload}
            retryLabel={tv("actions.retry")}
            testId="sa-affiliate-legacy-load-error"
            retryTestId="sa-affiliate-legacy-retry"
          />
        </div>
      ) : list.loaded && list.rows.length === 0 ? (
        <div className="p-8 text-center text-slate-400 text-sm" data-testid="sa-affiliate-legacy-empty">
          {t("saff.legacy.empty")}
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-slate-400 bg-slate-50">
                <tr>
                  <th className="text-left px-4 py-2">{t("saff.legacy.colAffiliate")}</th>
                  <th className="text-left px-4 py-2">{t("saff.legacy.colType")}</th>
                  <th className="text-right px-4 py-2">{t("saff.legacy.colAmount")}</th>
                  <th className="text-left px-4 py-2">{t("saff.legacy.colStatus")}</th>
                  <th className="text-left px-4 py-2">{t("saff.legacy.colNote")}</th>
                  <th className="text-left px-4 py-2">{t("saff.legacy.colCreatedAt")}</th>
                  <th className="text-left px-4 py-2">{t("saff.legacy.colArchivedAt")}</th>
                </tr>
              </thead>
              <tbody>
                {list.rows.map((r) => (
                  <tr key={r.id} className="border-t border-slate-100" data-testid={`sa-legacy-${r.id}`}>
                    <td className="px-4 py-2 text-slate-800">{r.affiliateTenantId}</td>
                    <td className="px-4 py-2 text-slate-500">{r.type}</td>
                    <td className="px-4 py-2 text-right tabular-nums">
                      {Number(r.amountLegacy).toFixed(2)} {String(r.currency || "").toUpperCase()}
                    </td>
                    <td className="px-4 py-2 text-slate-500">{r.status}</td>
                    <td className="px-4 py-2 text-slate-500">{r.note || EMPTY}</td>
                    <td className="px-4 py-2 text-slate-500">{fmtDate(r.createdAt, lang)}</td>
                    <td className="px-4 py-2 text-slate-500">{fmtDate(r.archivedAt, lang)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pager
            page={list.page}
            pageSize={list.pageSize}
            total={list.total}
            shown={list.rows.length}
            onPage={list.setPage}
            infoText={(vars) => t("saff.page.info", vars)}
            prevLabel={t("saff.page.prev")}
            nextLabel={t("saff.page.next")}
            testId="sa-legacy-page"
            tone="slate"
          />
        </>
      )}
    </section>
  );
}
